import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, RefreshCw, Archive, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button.tsx';
import {
  useArchiveProduct,
  useProduct,
  useRotateProductToken,
  useTheories,
} from '../../lib/hooks.ts';
import { CreateTheoryDialog } from './CreateTheoryDialog.tsx';
import { TokenRevealDialog } from './TokenRevealDialog.tsx';
import { ConfirmDialog } from '../../components/ui/confirm-dialog.tsx';

export function ProductPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);
  const [rotatedToken, setRotatedToken] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const product = useProduct(id);
  const theories = useTheories(id);
  const rotateMutation = useRotateProductToken();
  const archiveMutation = useArchiveProduct();

  if (product.isLoading) {
    return <div className="text-fg-muted">Загрузка…</div>;
  }
  if (!product.data) {
    return (
      <div className="card">
        <p className="mb-3 text-fg-muted">Продукт не найден.</p>
        <Link to="/" className="text-accent">
          ← К обзору
        </Link>
      </div>
    );
  }

  const p = product.data.product;
  const isArchived = p.archivedAt !== null;

  async function handleRotate(): Promise<void> {
    setConfirmRotate(false);
    const result = await rotateMutation.mutateAsync(id!);
    setRotatedToken(result.api_token);
  }

  async function handleArchive(): Promise<void> {
    setConfirmArchive(false);
    await archiveMutation.mutateAsync(id!);
    navigate('/', { replace: true });
  }

  return (
    <div>
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Обзор
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            {p.name}
            {isArchived ? (
              <span className="rounded bg-bg-elevated px-2 py-0.5 text-xs font-normal text-fg-muted">
                В архиве
              </span>
            ) : null}
          </h1>
          <div className="mt-1 font-mono text-sm text-fg-muted">{p.slug}</div>
        </div>
        {!isArchived ? (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setConfirmRotate(true)}>
              <RefreshCw className="h-4 w-4" />
              Перевыпустить токен
            </Button>
            <Button variant="ghost" onClick={() => setConfirmArchive(true)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </Button>
          </div>
        ) : null}
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Теории</h2>
        {!isArchived ? (
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Новая теория
          </Button>
        ) : null}
      </div>

      {theories.isLoading ? (
        <div className="text-fg-muted">Загрузка…</div>
      ) : theories.data?.items.length === 0 ? (
        <EmptyTheories isArchived={isArchived} onCreate={() => setCreateOpen(true)} />
      ) : (
        <div className="space-y-2">
          {theories.data?.items.map((t) => (
            <Link
              key={t.id}
              to={`/theories/${t.id}`}
              className="flex items-center justify-between rounded-lg border border-border bg-bg-soft p-4 transition-colors hover:bg-bg-elevated"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{t.name}</div>
                <div className="truncate font-mono text-xs text-fg-muted">{t.slug}</div>
              </div>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
            </Link>
          ))}
        </div>
      )}

      <CreateTheoryDialog open={createOpen} onOpenChange={setCreateOpen} productId={p.id} />

      <TokenRevealDialog
        open={rotatedToken !== null}
        token={rotatedToken}
        title="Новый API-токен"
        warning="Старый токен перестал работать. Сохрани новый и обнови его в коде сайта."
        onClose={() => setRotatedToken(null)}
      />

      <ConfirmDialog
        open={confirmRotate}
        title="Перевыпустить API-токен?"
        description="Старый токен перестанет работать сразу. Лендинг и любые другие интеграции отвалятся, пока ты не обновишь токен в их .env."
        confirmLabel="Перевыпустить"
        confirmVariant="primary"
        onConfirm={() => void handleRotate()}
        onCancel={() => setConfirmRotate(false)}
      />

      <ConfirmDialog
        open={confirmArchive}
        title="Архивировать продукт?"
        description="После архивации API-токен перестанет принимать события. Исторические данные останутся, но новые конверсии не записываются."
        confirmLabel="Архивировать"
        confirmVariant="danger"
        onConfirm={() => void handleArchive()}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
}

function EmptyTheories({
  isArchived,
  onCreate,
}: {
  isArchived: boolean;
  onCreate: () => void;
}): JSX.Element {
  if (isArchived) {
    return (
      <div className="card text-center text-fg-muted">
        Продукт в архиве — теорий нет, и новые создавать нельзя.
      </div>
    );
  }
  return (
    <div className="card flex flex-col items-center gap-3 py-10 text-center">
      <p className="max-w-md text-sm text-fg-muted">
        Теория — это гипотеза или кампания. Например: «весенний запуск», «рассылка по бесплатной
        рассыльной». Внутри теории — ссылки с разных источников трафика.
      </p>
      <Button variant="primary" onClick={onCreate}>
        <Plus className="h-4 w-4" />
        Создать первую теорию
      </Button>
    </div>
  );
}
